<template>
    <v-simple-table id="simple-table" dense>
        <template v-slot:default>
            <thead>
                <tr>
                    <th class="text-center font-size-md dark-blue-color" style="width: 25%"></th>
                    <th class="text-center font-size-md dark-blue-color" style="width: 15%">{{ $t('orders.totals.quantity') }}</th>
                    <th class="text-center font-size-md dark-blue-color" style="width: 15%">CX</th>
                    <th class="text-center font-size-md dark-blue-color" style="width: 15%">PL</th>
                    <th class="text-center font-size-md dark-blue-color" style="width: 15%">KG</th>

                    <template v-if="userStore.hasPermissionTo('View prices')">
                        <th class="text-center font-size-md dark-blue-color" style="width: 15%">{{ $t('orders.totals.netValue') }}</th>
                    </template>
                </tr>
            </thead>

            <tbody>
                <tr v-for="(item, index) in subtotals" :key="index">
                    <td class="text-right font-size-sm dark-blue-color px-1" style="height: 15px !important">Subtotal&nbsp;{{ item.name }}</td>
                    <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">{{ item.quantity }}</td>
                    <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">{{ item.numPackages }}</td>
                    <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">{{ item.numPallets }}</td>
                    <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">{{ parseFloat(item.weight).toFixed(2) }}</td>

                    <template v-if="userStore.hasPermissionTo('View prices')">
                        <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">{{ formatPrice(item.value) }}</td>
                    </template>
                </tr>

                <tr key="Total">
                    <td class="text-right font-size-sm dark-blue-color px-1" style="height: 15px !important"><strong>{{ $t('orders.totals.grandTotal') }}</strong></td>
                    <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">
                        <strong>{{ parseInt(shoppingCartQuantity()) }}</strong>
                    </td>
                    <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">
                        <strong>{{ parseInt(shoppingCartPackageAmount()) }}</strong>
                    </td>
                    <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">
                        <strong>{{ parseInt(shoppingCartPalletAmount()) }}</strong>
                    </td>
                    <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">
                        <strong>{{ parseFloat(shoppingCartWeight()).toFixed(2) }}</strong>
                    </td>

                    <template v-if="userStore.hasPermissionTo('View prices')">
                        <td class="text-center font-size-sm dark-blue-color px-1" style="height: 15px !important">
                            <strong>{{ formatPrice(shoppingCartTotal()) }}</strong>
                        </td>
                    </template>
                </tr>
            </tbody>
        </template>
    </v-simple-table>
</template>

<script>
import { mapStores } from 'pinia';
import { useUserStore } from '@/Stores/user';
import { useAffinityOrderStore } from '@/Stores/affinityorder';

export default {
    props: {
        affinityType: String,
    },

    data: () => ({
        defaultTotalRow: {
            name: '',
            quantity: 0,
            numPackages: 0,
            numPallets: 0,
            weight: 0,
            value: 0,
        },
    }),

    computed: {
        ...mapStores(useAffinityOrderStore, useUserStore),

        subtotals: {
            get() {
                return this.affinityOrderStore.getSubTotals;
            },
            set(subtotals) {
                this.affinityOrderStore.setSubTotals(subtotals);
            },
        },
    },

    watch: {
        'affinityOrderStore.tabs': {
            handler: function (tabs) {
                if (tabs.length === 0) return;

                const firstOrderDiscount = this.getFirstOrderDiscount();
                const productsAffinity = this.affinityOrderStore.getProducts;

                this.invokeCalculateSubTotals(tabs, productsAffinity, firstOrderDiscount);
            },
        },

        'affinityOrderStore._getCart.products': {
            handler: function (productsAffinity) {
                const firstOrderDiscount = this.getFirstOrderDiscount();
                const tabs = this.affinityOrderStore.getTabs;

                this.invokeCalculateSubTotals(tabs, productsAffinity, firstOrderDiscount);
            },
            deep: true,
            immediate: true,
        },
    },

    methods: {
        createBrandTotalRows(tabs) {
            this.affinityOrderStore.removeAllSubTotals();

            Object.keys(tabs).forEach((index) => {
                let affinityTab = this.affinityOrderStore.getTabByIndex(index);

                if (affinityTab.tab !== 'Geral') {
                    let newRow = JSON.parse(JSON.stringify(this.defaultTotalRow));
                    newRow.name = affinityTab.tab;

                    this.affinityOrderStore.addSubTotal(newRow);
                }
            });
        },

        invokeCalculateSubTotals(tabs, productsAffinity, firstOrderDiscount) {
            this.createBrandTotalRows(tabs);

            Object.keys(tabs).forEach((index) => {
                let affinityTab = this.affinityOrderStore.getTabByIndex(index);

                productsAffinity.forEach((product) => {
                    if (affinityTab.tab === product.brand && this.affinityType === product.enc_type) {
                        this.calculateSubTotalByBrand(index, product);
                    }
                });
            });

            window.EventBus.$emit('apply-products-discount', productsAffinity, firstOrderDiscount);
        },

        calculateSubTotalByBrand(index, product) {
            let subtotals = this.affinityOrderStore.getSubTotals;

            // @Note: Subtract -1 because the index starts at 0 ('Geral' tab)
            index = index == 0 ? 0 : index - 1;

            subtotals[index].quantity += product.quantity;
            subtotals[index].numPackages += product.amount_per_package > 0 ? Math.floor(product.quantity / product.amount_per_package) : 0;
            subtotals[index].numPallets += product.amount_per_pallet > 0 ? Math.floor(product.quantity / product.amount_per_pallet) : 0;
            subtotals[index].weight += product.quantity * product.weight;
            subtotals[index].value += this.formatDecimal(product.discounted_price, 2);

            this.affinityOrderStore.updateSubTotal(index, subtotals[index]);
        },

        getFirstOrderDiscount() {
            let firstOrderDiscount = 0;
            if (this.affinityOrderStore.getFirstOrderDiscount && 'percentage' in this.affinityOrderStore.getFirstOrderDiscount) {
                firstOrderDiscount = this.affinityOrderStore.getFirstOrderDiscount.percentage / 100 ?? 0;
            }

            return firstOrderDiscount;
        },

        shoppingCartQuantity() {
            const fn = (item) => (item.enc_type == this.affinityType ? item.quantity : 0);
            return this.affinityOrderStore.getProductSum(fn);
        },

        shoppingCartPackageAmount() {
            const fn = (item) => (item.enc_type == this.affinityType && item.amount_per_package > 0 ? Math.floor(item.quantity / item.amount_per_package) : 0);
            return this.affinityOrderStore.getProductSum(fn);
        },

        shoppingCartPalletAmount() {
            const fn = (item) => (item.enc_type == this.affinityType && item.amount_per_pallet > 0 ? Math.floor(item.quantity / item.amount_per_pallet) : 0);
            return this.affinityOrderStore.getProductSum(fn);
        },

        shoppingCartWeight() {
            const fn = (item) => (item.enc_type == this.affinityType ? item.quantity * item.weight : 0);
            return this.affinityOrderStore.getProductSum(fn, 2);
        },

        shoppingCartTotal() {
            const fn = (item) => (item.enc_type == this.affinityType && item.discounted_price > 0 ? this.formatDecimal(item.discounted_price, 2) : 0);
            return this.affinityOrderStore.getProductSum(fn, 3);
        },
    },

    mounted() {
        window.EventBus.$on('recalc-discounts', () => {
            const firstOrderDiscount = this.getFirstOrderDiscount();
            const tabs = this.affinityOrderStore.getTabs;
            const productsAffinity = this.affinityOrderStore.getProducts;

            this.invokeCalculateSubTotals(tabs, productsAffinity, firstOrderDiscount);
        });
    },

    beforeDestroy() {
        window.EventBus.$off('recalc-discounts');
    },
};
</script>

<style>
#simple-table.v-data-table {
    line-height: 25px !important;
}
</style>
